import React, { useCallback, useMemo, useState, Fragment, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { DeviceList } from 'app/home/main-pages/devices/Devices'
import { BindList } from 'app/home/main-pages/binds/Binds';
import { checkAndTransformDeviceForScenario } from 'app/model/Scenario';
import { CurtainState, DeviceType, useDeviceStub } from 'app/model/Device';
import { useLocalizedStrings } from 'app/localization/Context';
import Toggle from 'app/home/general-components/Toggle/Toggle';
import useWindowDimensions from 'app/home/general-components/WindowDimensions';
import { CurrentPageContext } from 'app/home/general-components/HistoryStackContext';

import LampOrRelayControl, { Curtain1Lamp2Control } from '../../device/deivce-detail/device-control/LampControl';
import { PlugControl } from '../../device/deivce-detail/device-control/PlugControl';
import { Split } from '../../device/deivce-detail/device-control/remote/RemoteControl';
import { EcoThermostatControl, MarsusControl, SednaControl, ThermostatOnOff } from '../../device/deivce-detail/device-control/Thermostat/ThermostatControl';
import { TVAskStatePage } from '../../bind/remote-special/RemoteSpecial';
import CurtainControl from '../../device/deivce-detail/device-control/CurtainControl';
import { CoolerScenarioSpecialSlection } from '../../device/deivce-detail/device-control/CoolerControl';
import { WCScenarioSpecialSlection } from '../../device/deivce-detail/device-control/WCControl';
import ElevatorControl from '../../device/deivce-detail/device-control/ElevatorControl';
import DoorbellControl from '../../device/deivce-detail/device-control/DoorbellControl';

import { ReactComponent as IconSirenOnRed } from 'images/device/status-icon/siren-on-red.svg';
import { ReactComponent as IconSirenOffGray } from 'images/device/status-icon/siren-off-gray.svg';

import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconXBlack } from 'images/general/x-black.svg';
import { ReactComponent as IconTickWhite } from 'images/general/tick-white.svg';
import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';


export function SelectDevices(
    { dismissHandler, pageData, setPageData, back,
        intrestedDevice, setIntrestedDevice,
        devices, deviceActions, setDeviceActions,
        negotiatedProtocol }) {

    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const dismissAskStatePage = () => { back(); };

    const selectionCallback = useCallback(device => {
        const index = deviceActions.findIndex(
            (action) => action.mac === device.mac);
        if (index >= 0) {
            const _deviceActions = [...deviceActions];
            _deviceActions.splice(index, 1);
            setDeviceActions(_deviceActions);
        } else {
            setIntrestedDevice({
                name: device.name,
                mac: device.mac,
                type: device.type,
                sub_type: device.sub_type,
                num_channels: device.num_channels,
                config_per_mode: device.config_per_mode,
                set_point_min: device.set_point_min,
                set_point_max: device.set_point_max,
                use_custom_switch_names: device.use_custom_switch_names,
                switch1_name: device.switch1_name,
                switch2_name: device.switch2_name,
                switch3_name: device.switch3_name,
                switch4_name: device.switch4_name,
            });
        }
    }, [deviceActions, setDeviceActions, setIntrestedDevice]);

    const devicesForList = useMemo(() => {
        let _devicesForList = [];
        for (let i = 0; i < devices.length; i++) {
            const device = devices[i];
            const _device = checkAndTransformDeviceForScenario(negotiatedProtocol, device);
            if (_device) {
                _devicesForList.push(_device);
            }
        }
        return _devicesForList;
    }, [devices, negotiatedProtocol])

    const selectedDevices = useMemo(() => {
        return deviceActions.map(action => action.mac);
    }, [deviceActions]);

    const nextClick = useCallback(() => {
        setPageData({ name: 'select-binds', from: pageData.name });
    }, [pageData, setPageData]);
    const next2Click = useCallback(() => {
        setPageData({ name: 'overview', from: pageData.name });
    }, [pageData, setPageData]);

    const { height: windowHeight } = useWindowDimensions();
    // marsus fancoil view height is about 751.
    const isFull = intrestedDevice && (
        ((intrestedDevice.type === DeviceType.marsus) && (p.inOverlayPage || (windowHeight <= 770))) ||
        (intrestedDevice.type === DeviceType.sedna) ||
        (intrestedDevice.type === DeviceType.hinavaEcoThermostat) ||
        ((intrestedDevice.type === DeviceType.splitRemote) && (p.inOverlayPage || (windowHeight <= 700)))
    );

    return (
        <div className="create-scenario-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{sc.selectDevices}</div>
            <div className="info" style={{ marginBottom: "8px" }}>{sc.selectDevicesInfo}</div>

            <div className="the-rest-of-page-container with-full-button-margin">
                <DeviceList
                    // bottomPaddingSize={64 + 16 + 8} // +8 for safe-area
                    devices={devicesForList}
                    selectedDevices={selectedDevices}
                    selectionCallback={selectionCallback}
                    showStates={false}
                    dontOpenSettings={true}
                    showSubDevices={true} />
            </div>

            {
                (pageData.forEdit) ? (
                    <div className="blue-button next-button" onClick={next2Click}>
                        <div>{sc.backToOverview}</div> <IconBackBlack className="icon-180" />
                    </div>
                ) : (
                    <div className="blue-button next-button" onClick={nextClick}>
                        <div>{sc.next}</div> <IconBackBlack className="icon-180" />
                    </div>
                )
            }


            <AskState
                isFull={isFull}
                dismiss={dismissAskStatePage}
                negotiatedProtocol={negotiatedProtocol}
                intrestedDevice={intrestedDevice}
                setDeviceActions={setDeviceActions} />
        </div>
    );
}

export function SelectBinds(
    { dismissHandler, pageData, setPageData, back,
        intrestedBind, setIntrestedBind,
        binds, devices, scenarios, bindStates, setBindStates }) {

    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const dismissAskStatePage = () => { back(); };

    const selectionCallback = useCallback(bind => {
        const index = bindStates.findIndex(
            (b) => b.id === bind.id);
        if (index >= 0) {
            const _bindStates = [...bindStates];
            _bindStates.splice(index, 1);
            setBindStates(_bindStates);
        } else {
            setIntrestedBind({ id: bind.id });
        }
    }, [bindStates, setBindStates, setIntrestedBind]);

    const selectedBinds = useMemo(() => {
        return bindStates.map(b => b.id);
    }, [bindStates]);

    const nextClick = useCallback(() => {
        setPageData({ name: 'overview', from: pageData.name });
    }, [pageData, setPageData]);

    return (
        <div className="create-scenario-page-content">
            {
                (pageData.forEdit) ? (
                    <Fragment>
                        <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />
                    </Fragment>
                ) : (
                    <Fragment>
                        <IconBack className="nav-icon" /> <div className="nav-button" onClick={() => { back(); }} />
                        <IconX className="nav-icon-secondary" /> <div className="nav-button-secondary" onClick={dismissHandler} />
                    </Fragment>
                )
            }

            <div className="header">{sc.selectBinds}</div>
            <div className="info" style={{ marginBottom: "8px" }}>{sc.selectBindsInfo}</div>

            <div className="the-rest-of-page-container with-full-button-margin">
                <BindList
                    scenarios={scenarios}
                    devices={devices}
                    // bottomPaddingSize={64 + 16 + 8} // +8 for safe-area
                    binds={binds}
                    selectionCallback={selectionCallback} dontShowEnables
                    selectedBinds={selectedBinds} />
            </div>


            {
                (pageData.forEdit) ? (
                    <div className="blue-button next-button" onClick={nextClick}>
                        <div>{sc.backToOverview}</div> <IconBackBlack className="icon-180" />
                    </div>
                ) : (
                    <div className="blue-button next-button" onClick={nextClick}>
                        <div>{sc.next}</div> <IconBackBlack className="icon-180" />
                    </div>
                )
            }

            <AskState dismiss={dismissAskStatePage}
                intrestedBind={intrestedBind}
                setBindStates={setBindStates} />
        </div>
    );
}

function AskState({ isFull, dismiss, intrestedDevice, setDeviceActions, intrestedBind, setBindStates, negotiatedProtocol }) {
    const deviceSpecified = (intrestedDevice !== undefined && intrestedDevice !== null);
    const bindSpecified = (intrestedBind !== undefined && intrestedBind !== null);
    const show = deviceSpecified || bindSpecified;
    return (
        <AnimatePresence>
            {show && (<motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={{
                    initial: {
                        opacity: 0
                    },
                    animate: {
                        opacity: 1
                    },
                    exit: {
                        opacity: 0
                    }
                }}
                transition={{
                    ease: "easeInOut",
                    duration: 0.65,
                }}
                className="ask-state-page-back"
                key="scenario-select-state-page-back"

                onClick={dismiss}
            />)}
            {show && (<motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={{
                    initial: {
                        transform: "translateY(100%)"
                    },
                    animate: {
                        transform: "translateY(0%)"
                    },
                    exit: {
                        transform: "translateY(100%)"
                    }
                }}
                transition={{
                    ease: "easeInOut",
                    duration: 0.65,
                }}
                className={isFull ? "ask-state-page-front full" : "ask-state-page-front"}
                key="scenario-select-state-page-front"
            >
                {deviceSpecified ? React.createElement(AskDeviceStateInnerSection, { dismiss, intrestedDevice, setDeviceActions, negotiatedProtocol }) : null}
                {bindSpecified ? React.createElement(AskBindStateInnerSection, { dismiss, intrestedBind, setBindStates }) : null}
            </motion.div>)}
        </AnimatePresence>
    )
}

function AskDeviceStateInnerSection({ intrestedDevice, dismiss, setDeviceActions, negotiatedProtocol }) {
    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const [deviceToShow, deviceToAdd] = useDeviceStub(intrestedDevice);
    const [tvDataToSend, tvSetDataToSend] = useState(undefined);
    const [tvErrorChannelIsEmpty, tvSetErrorChannelIsEmpty] = useState(false);

    const [coolerDataToSend, coolerSetDataToSend] = useState(undefined);
    const [wcDataToSend, wcSetDataToSend] = useState(undefined);

    // console.log("intrestedDevice", intrestedDevice);

    const setCallback = useCallback(() => {
        // TODO: known bug: sedna device has extra fileds when 'is_on === false'.

        switch (intrestedDevice.type) {
            case DeviceType.evaporativeCooler:
                setDeviceActions(actions => [...actions, {
                    mac: intrestedDevice.mac,
                    type: intrestedDevice.type,
                    ...coolerDataToSend
                }]);
                dismiss();
                break;
            case DeviceType.wc:
                setDeviceActions(actions => [...actions, {
                    mac: intrestedDevice.mac,
                    type: intrestedDevice.type,
                    ...wcDataToSend
                }]);
                dismiss();
                break;
            case DeviceType.tvRemote:
                if (tvErrorChannelIsEmpty !== true) {
                    // console.log("tvDataToSend", tvDataToSend);
                    setDeviceActions(actions => [...actions, {
                        mac: intrestedDevice.mac,
                        type: intrestedDevice.type,
                        ...tvDataToSend
                    }]);
                    dismiss();
                }
                break;
            case DeviceType.dpst:
                if ((deviceToAdd["state1"] !== undefined) ||
                    (deviceToAdd["state2"] !== undefined)) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.lampX3:
                if ((deviceToAdd["state1"] !== undefined) ||
                    (deviceToAdd["state2"] !== undefined) ||
                    (deviceToAdd["state3"] !== undefined)) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.lampX4:
                if ((deviceToAdd["state1"] !== undefined) ||
                    (deviceToAdd["state2"] !== undefined) ||
                    (deviceToAdd["state3"] !== undefined) ||
                    (deviceToAdd["state4"] !== undefined)) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.curtain1Lamp2:
                if ((deviceToAdd["lamp1"] !== undefined) ||
                    (deviceToAdd["lamp2"] !== undefined) ||
                    (deviceToAdd["curtain"] !== undefined)) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.relay4:
                if ((deviceToAdd["channel1"] !== undefined) ||
                    (deviceToAdd["channel2"] !== undefined) ||
                    (deviceToAdd["channel3"] !== undefined) ||
                    (deviceToAdd["channel4"] !== undefined)) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.curtain:
                if (
                    ((deviceToAdd["channel1"] !== CurtainState.idle) &&
                        (deviceToAdd["channel1"] !== undefined))
                    ||
                    ((deviceToAdd["channel2"] !== CurtainState.idle) &&
                        (deviceToAdd["channel2"] !== undefined))) {
                    setDeviceActions(actions => [...actions, deviceToAdd]);
                }
                dismiss();
                break;
            case DeviceType.plug:
            case DeviceType.doorbell:
            case DeviceType.elevator1:
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.siren:
            case DeviceType.splitRemote:
            case DeviceType.marsus:
            case DeviceType.sedna:
            case DeviceType.hinavaEcoThermostat:
            default:
                setDeviceActions(actions => [...actions, deviceToAdd]);
                dismiss();
                break;
        }
    }, [dismiss, setDeviceActions, deviceToAdd, tvErrorChannelIsEmpty,
        tvDataToSend, coolerDataToSend, wcDataToSend, intrestedDevice]);

    const tvYouMustEnterANumber = s.createBindProcess.tvYouMustEnterANumber;

    const control = useMemo(() => {
        switch (deviceToShow.type) {
            case DeviceType.plug:
                return <div className="control-container">
                    <PlugControl device={deviceToShow} darkerBorder /></div>;
            case DeviceType.curtain1Lamp2:
                return <div className="control-container">
                    <Curtain1Lamp2Control device={deviceToShow} darkerBorder
                        canStateBeNoChange={negotiatedProtocol.hasDontChangeInScenario} /></div>;
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.dpst:
            case DeviceType.lampX3:
            case DeviceType.lampX4:
            case DeviceType.relay4:
                return <div className="control-container">
                    <LampOrRelayControl device={deviceToShow} darkerBorder
                        canStateBeNoChange={negotiatedProtocol.hasDontChangeInScenario} /></div>;
            case DeviceType.evaporativeCooler:
                return <CoolerScenarioSpecialSlection setData={coolerSetDataToSend} />
            case DeviceType.elevator1:
                return <div className="control-container"><ElevatorControl device={deviceToShow} darkerBorder /></div>
            case DeviceType.doorbell:
                return <div className="control-container"><DoorbellControl device={deviceToShow} darkerBorder /></div>
            case DeviceType.wc:
                return <WCScenarioSpecialSlection setData={wcSetDataToSend} />
            // return <div className="control-container"><CoolerControl device={deviceToShow} darkerBorder /></div>;
            case DeviceType.curtain:
                return <div className="control-container">
                    <CurtainControl device={deviceToShow} darkerBorder /></div>;
            case DeviceType.siren:
                return <SirenControl device={deviceToShow} />;
            case DeviceType.splitRemote:
                return <div className="control-box-container">
                    <Split
                        lightAndMinimal={true}
                        device={deviceToShow}
                        sendCommandH={(func) => {
                            return () => {
                                deviceToShow.changeState({ 'function': func });
                            }
                        }} />
                </div>;
            case DeviceType.tvRemote:
                return <div className="tv-box-container">
                    <TVAskStatePage setData={tvSetDataToSend} setErrorChannelIsEmpty={tvSetErrorChannelIsEmpty} />
                    {tvErrorChannelIsEmpty ? <div className="tv-error-label">{tvYouMustEnterANumber}</div> : null}
                </div>;
            case DeviceType.marsus:
                return <div className="thermostat-container">
                    <ThermostatOnOff device={deviceToShow} />
                    <MarsusControl device={deviceToShow} forLightBackground={true} />
                </div>;
            case DeviceType.sedna:
                return <div className="thermostat-container">
                    <ThermostatOnOff device={deviceToShow} />
                    <SednaControl device={deviceToShow} forLightBackground={true} />
                </div>;
            case DeviceType.hinavaEcoThermostat:
                return <div className="thermostat-container">
                    <ThermostatOnOff device={deviceToShow} />
                    <EcoThermostatControl device={deviceToShow} forLightBackground={true} />
                </div>;
            default:
                return null;
        }
    }, [deviceToShow, tvErrorChannelIsEmpty, tvYouMustEnterANumber, negotiatedProtocol.hasDontChangeInScenario]);

    return (
        <Fragment>
            <IconXBlack className="dismiss-button" onClick={dismiss} />
            <div className="header">{sc.setState}</div>
            {control}
            <div className="set-button" onClick={setCallback}
                style={{ opacity: (tvErrorChannelIsEmpty !== true) ? 1 : 0.5 }}>
                <div className="label">{sc.set}</div>
                <IconTickWhite className="icon" />
            </div>
        </Fragment>
    );
}

function SirenControl({ device }) {
    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const onClickH = useCallback((key) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}
            changeStateObject[key] = !device[key]
            device.changeState(changeStateObject);
        }
    }, [device]);

    return (
        <div className="siren-select-state">
            <div className="text">{sc.sirenNotAlarming}</div>
            <IconSirenOffGray className="icon" />
            <Toggle onClick={onClickH("state")} enable={device.state} />
            <IconSirenOnRed className="icon" />
            <div className="text-red">{sc.sirenAlarming}</div>
        </div>
    );
}

function AskBindStateInnerSection({ intrestedBind, dismiss, setBindStates }) {
    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const [intrestedBindState, setIntrestedBindState] = useState({ id: intrestedBind.id, enable: true });

    const onEnableClick = useCallback(() => {
        setIntrestedBindState({ id: intrestedBindState.id, enable: !intrestedBindState.enable })
    }, [intrestedBindState]);

    const setCallback = useCallback(() => {
        setBindStates(states => [...states, intrestedBindState]);
        dismiss();
    }, [dismiss, setBindStates, intrestedBindState]);

    return (
        <Fragment>
            <div className="bind-control-container">
                <div className="label">{intrestedBindState.enable ? sc.enable : sc.disable}</div>
                <div onClick={onEnableClick} className={"bind-cell-toggle " + (intrestedBindState.enable ? "active" : "inactive")}>
                    <div className={"bind-cell-toggle-head off " + (intrestedBindState.enable ? "inactive" : "active")} />
                    <div className={"bind-cell-toggle-head on " + (intrestedBindState.enable ? "active" : "inactive")} />
                </div>
            </div>

            <div className="set-button" onClick={setCallback}>
                <div className="label" >{sc.set}</div>
                <IconTickWhite className="icon" />
            </div>
        </Fragment>
    );
}
